import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, Plus } from "lucide-react";
import { branchesApi, type BranchSummary } from "../../shared/api/branches";
import {
  displayTokensApi,
  kioskUrlFor,
  type DisplayTokenView,
} from "../../shared/api/displayTokens";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { copyText } from "../../shared/clipboard";

/**
 * /settings/kiosks — issue + revoke kiosk tokens for waiting-room TVs.
 *
 * <p>The token IS the credential; once revoked the row stays as audit
 * evidence (status=Đã thu hồi) but never authenticates again. Clinics
 * normally issue one per TV with a label like "Phòng chờ tầng 1" so the
 * operator can identify the right row when a TV is moved or replaced.
 */
export function KiosksSettingsPage() {
  const qc = useQueryClient();

  const tokens = useQuery({
    queryKey: ["display-tokens"],
    queryFn: () => displayTokensApi.list(),
  });

  const branches = useQuery<BranchSummary[]>({
    queryKey: ["branches", "for-kiosks"],
    queryFn: () => branchesApi.list(),
  });

  const revoke = useMutation({
    mutationFn: (token: string) => displayTokensApi.revoke(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["display-tokens"] }),
  });

  const [showIssue, setShowIssue] = useState(false);
  const [justIssued, setJustIssued] = useState<DisplayTokenView | null>(null);

  return (
    <div className="font-sans">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground">
              Màn hình hàng đợi (Kiosk)
            </h2>
            <Button type="button" onClick={() => setShowIssue(true)}>
              <Plus className="h-4 w-4" /> Cấp token mới
            </Button>
          </div>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            Cấp token cho TV phòng chờ. Token có thể dùng làm URL công khai để TV
            tự động hiển thị hàng đợi, không cần đăng nhập nhân viên.
          </p>

          {tokens.isLoading && (
            <div className="text-sm text-muted-foreground">Đang tải…</div>
          )}
          {tokens.isError && (
            <div className="text-sm text-destructive">
              Không tải được danh sách token.
            </div>
          )}

          {tokens.data && tokens.data.length === 0 && (
            <div className="text-sm text-muted-foreground">
              Chưa có TV kiosk nào được cấp token.
            </div>
          )}

          {tokens.data && tokens.data.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tên màn hình</TableHead>
                    <TableHead>Chi nhánh</TableHead>
                    <TableHead>Hết hạn</TableHead>
                    <TableHead>Lần dùng gần nhất</TableHead>
                    <TableHead>Trạng thái</TableHead>
                    <TableHead className="w-[200px]">URL</TableHead>
                    <TableHead className="w-[110px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.data.map((t) => (
                    <Row
                      key={t.token}
                      token={t}
                      branchLabel={
                        branches.data?.find((b) => b.id === t.branchId)?.name ??
                        "—"
                      }
                      onRevoke={() => {
                        if (
                          confirm(
                            `Thu hồi token "${t.label}"? Không thể hoàn tác.`,
                          )
                        ) {
                          revoke.mutate(t.token);
                        }
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <IssueModal
        open={showIssue}
        branches={branches.data ?? []}
        onClose={() => setShowIssue(false)}
        onIssued={(t) => {
          setShowIssue(false);
          setJustIssued(t);
          qc.invalidateQueries({ queryKey: ["display-tokens"] });
        }}
      />

      {justIssued && (
        <JustIssuedModal
          token={justIssued}
          onClose={() => setJustIssued(null)}
        />
      )}
    </div>
  );
}

function Row({
  token,
  branchLabel,
  onRevoke,
}: {
  token: DisplayTokenView;
  branchLabel: string;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = kioskUrlFor(token.token);
  return (
    <TableRow>
      <TableCell className="font-medium text-foreground">
        {token.label}
      </TableCell>
      <TableCell>{branchLabel}</TableCell>
      <TableCell>
        {new Date(token.expiresAt).toLocaleDateString("vi-VN")}
      </TableCell>
      <TableCell>
        {token.lastUsedAt ? (
          new Date(token.lastUsedAt).toLocaleString("vi-VN")
        ) : (
          <span className="text-muted-foreground">Chưa dùng</span>
        )}
      </TableCell>
      <TableCell>
        {token.revoked ? (
          <Badge variant="destructive">Đã thu hồi</Badge>
        ) : new Date(token.expiresAt) < new Date() ? (
          <Badge variant="warning">Hết hạn</Badge>
        ) : (
          <Badge variant="success">Hoạt động</Badge>
        )}
      </TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={token.revoked}
          onClick={() => {
            void copyText(url).then((ok) => {
              if (!ok) return;
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" /> Đã copy
            </>
          ) : (
            <>
              <ClipboardCopy className="h-4 w-4" /> Copy URL
            </>
          )}
        </Button>
      </TableCell>
      <TableCell>
        {!token.revoked && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onRevoke}
          >
            Thu hồi
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function IssueModal({
  open,
  branches,
  onClose,
  onIssued,
}: {
  open: boolean;
  branches: BranchSummary[];
  onClose: () => void;
  onIssued: (t: DisplayTokenView) => void;
}) {
  const [branchId, setBranchId] = useState<string>("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const issue = useMutation({
    mutationFn: () =>
      displayTokensApi.issue({
        branchId,
        label: label.trim(),
      }),
    onSuccess: (t) => {
      setBranchId("");
      setLabel("");
      setError(null);
      onIssued(t);
    },
    onError: (e) => {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Không cấp được token.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Cấp token kiosk mới</DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="kiosk-branch">Chi nhánh</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger id="kiosk-branch">
              <SelectValue placeholder="Chọn chi nhánh…" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="kiosk-label">Tên màn hình</Label>
          <Input
            id="kiosk-label"
            type="text"
            placeholder="VD: TV phòng chờ tầng 1"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={128}
          />
          <small className="text-sm text-muted-foreground">
            Đặt tên dễ nhận biết để quản lý nhiều TV.
          </small>
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button
            type="button"
            disabled={!branchId || !label.trim() || issue.isPending}
            onClick={() => issue.mutate()}
          >
            {issue.isPending ? "Đang cấp…" : "Cấp token"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JustIssuedModal({
  token,
  onClose,
}: {
  token: DisplayTokenView;
  onClose: () => void;
}) {
  const url = kioskUrlFor(token.token);
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>✓ Đã cấp token: {token.label}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Copy URL phía dưới và mở trên trình duyệt của TV phòng chờ. TV sẽ
          tự động làm mới hàng đợi mỗi 10 giây.
        </p>

        <div className="break-all rounded-md bg-muted p-3 font-mono text-[13px]">
          {url}
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={() => {
              void copyText(url).then((ok) => {
                if (!ok) return;
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" /> Đã copy
              </>
            ) : (
              <>
                <ClipboardCopy className="h-4 w-4" /> Copy URL
              </>
            )}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Đóng
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
